/**
 * Unified Cache Service
 * 단일 인터페이스로 SQLite(Electron)와 IndexedDB(Browser) 관리
 */

interface CacheBackend {
  init(vaultPath: string): Promise<void>
  getTree(): Promise<any[]>
  updateNode(path: string, data: any): Promise<void>
  deleteNode(path: string): Promise<void>
  validateCache(): Promise<boolean>
  clear(): Promise<void>
}

class UnifiedCache {
  private backend: CacheBackend
  private lastValidation = 0
  private VALIDATION_INTERVAL = 60 * 60 * 1000 // 60분으로 증가
  public initialized = false
  
  constructor() {
    // 환경에 따라 백엔드 자동 선택
    const isElectron = typeof window !== 'undefined' && window.api?.getCachedTree
    
    if (isElectron) {
      this.backend = new ElectronCacheBackend()
    } else {
      this.backend = new BrowserCacheBackend()
    }
  }
  
  async init(vaultPath: string): Promise<void> {
    await this.backend.init(vaultPath)
    this.initialized = true
  }
  
  async getTree(): Promise<any[]> {
    // 주기적 무결성 검증 - 하지만 너무 자주 하지 않도록
    if (Date.now() - this.lastValidation > this.VALIDATION_INTERVAL) {
      const isValid = await this.backend.validateCache()
      if (!isValid) {
        // 캐시를 지우지 말고 그냥 경고만 출력
        console.warn('Cache may be stale, consider refreshing')
        // 캐시를 지우면 성능 문제가 발생하므로 유지
      }
      this.lastValidation = Date.now()
    }
    
    return await this.backend.getTree()
  }
  
  // Compatibility methods for existing code
  async getFullTree(): Promise<any[]> {
    return this.getTree()
  }
  
  async setFullTree(nodes: any[]): Promise<void> {
    // For browser, we need to store in IndexedDB
    if (this.backend instanceof BrowserCacheBackend) {
      await (this.backend as BrowserCacheBackend).setFullTree(nodes)
    }
    // For Electron, it's handled by IPC/main process
  }
  
  async getStats(): Promise<any> {
    // Get stats from backend if available
    if (this.backend instanceof BrowserCacheBackend) {
      return (this.backend as BrowserCacheBackend).getStats()
    }
    // For Electron, return basic stats
    return {
      lastFullScan: this.lastValidation || Date.now(),
      lastUpdate: Date.now()
    }
  }
  
  async updateNode(path: string, data: any): Promise<void> {
    try {
      await this.backend.updateNode(path, data)
    } catch (error) {
      console.error('Cache update failed:', error)
      // Fallback: mark cache as invalid
      this.lastValidation = 0
    }
  }
  
  async deleteNode(path: string): Promise<void> {
    try {
      await this.backend.deleteNode(path)
    } catch (error) {
      console.error('Cache delete failed:', error)
      this.lastValidation = 0
    }
  }
  
  async clear(): Promise<void> {
    try {
      await this.backend.clear()
      this.lastValidation = 0
    } catch (error) {
      console.error('Cache clear failed:', error)
    }
  }
}

// Electron 백엔드 (IPC를 통한 SQLite)
class ElectronCacheBackend implements CacheBackend {
  async init(vaultPath: string): Promise<void> {
    // Already initialized in main process
  }
  
  async getTree(): Promise<any[]> {
    if (!window.api?.getCachedTree) return []
    const response = await window.api.getCachedTree({ path: '/' })
    return response.nodes || []
  }
  
  async updateNode(path: string, data: any): Promise<void> {
    // IPC를 통해 main process의 SQLite 업데이트
    if (!window.api?.updateCacheNode) return
    await window.api.updateCacheNode(path, data)
  }
  
  async deleteNode(path: string): Promise<void> {
    if (!window.api?.deleteCacheNode) return
    await window.api.deleteCacheNode(path)
  }
  
  async validateCache(): Promise<boolean> {
    // 랜덤 샘플링으로 파일 존재 확인
    if (!window.api?.validateCache) return false
    const response = await window.api.validateCache()
    return response?.valid || false
  }
  
  async clear(): Promise<void> {
    if (!window.api?.clearCache) return
    await window.api.clearCache()
  }
}

// 브라우저 백엔드 (IndexedDB)
class BrowserCacheBackend implements CacheBackend {
  private db: IDBDatabase | null = null
  
  async init(vaultPath: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(`vault-cache-${vaultPath}`, 1)
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains('nodes')) {
          db.createObjectStore('nodes', { keyPath: 'path' })
        }
        if (!db.objectStoreNames.contains('stats')) {
          db.createObjectStore('stats', { keyPath: 'id' })
        }
      }
      
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }
      
      request.onerror = () => reject(request.error)
    })
  }
  
  async getTree(): Promise<any[]> {
    if (!this.db) {
      console.debug('BrowserCacheBackend: Database not initialized yet')
      return []
    }
    
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(['nodes'], 'readonly')
      const store = tx.objectStore('nodes')
      const request = store.getAll()
      
      request.onsuccess = () => {
        const nodes = request.result
        // Build tree structure from flat nodes
        resolve(this.buildTree(nodes))
      }
      request.onerror = () => {
        console.debug('Failed to get cached nodes:', request.error)
        resolve([])  // Return empty array instead of rejecting
      }
    })
  }
  
  async updateNode(path: string, data: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(['nodes'], 'readwrite')
      const store = tx.objectStore('nodes')
      const request = store.put({ path, ...data })
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }
  
  async deleteNode(path: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(['nodes'], 'readwrite')
      const store = tx.objectStore('nodes')
      const request = store.delete(path)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }
  
  async validateCache(): Promise<boolean> {
    // 브라우저에서는 File System API로 샘플 체크
    try {
      const stats = await this.getStats()
      if (!stats) return false
      
      // 캐시가 너무 오래됨
      if (Date.now() - stats.lastUpdate > 2 * 60 * 60 * 1000) {
        return false
      }
      
      // TODO: 실제 파일 몇 개 샘플링 체크
      return true
    } catch {
      return false
    }
  }
  
  async clear(): Promise<void> {
    if (!this.db) return
    
    const tx = this.db.transaction(['nodes', 'stats'], 'readwrite')
    await tx.objectStore('nodes').clear()
    await tx.objectStore('stats').clear()
  }
  
  async setFullTree(nodes: any[]): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    // Clear and rebuild
    const tx = this.db.transaction(['nodes', 'stats'], 'readwrite')
    const nodeStore = tx.objectStore('nodes')
    const statsStore = tx.objectStore('stats')
    
    // Clear existing nodes
    await nodeStore.clear()
    
    // Flatten tree and store
    const flatNodes = this.flattenTree(nodes)
    for (const node of flatNodes) {
      await nodeStore.put(node)
    }
    
    // Update stats
    await statsStore.put({
      id: 'main',
      lastFullScan: Date.now(),
      lastUpdate: Date.now(),
      totalFiles: flatNodes.filter(n => n.type === 'file').length,
      totalFolders: flatNodes.filter(n => n.type === 'folder').length
    })
  }
  
  private flattenTree(nodes: any[], parentPath?: string): any[] {
    const result: any[] = []
    for (const node of nodes) {
      result.push({
        ...node,
        path: node.path || node.id,
        parentPath
      })
      if (node.children) {
        result.push(...this.flattenTree(node.children, node.path || node.id))
      }
    }
    return result
  }
  
  async getStats(): Promise<any> {
    if (!this.db) return null
    
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(['stats'], 'readonly')
      const store = tx.objectStore('stats')
      const request = store.get('main')
      
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }
  
  private buildTree(nodes: any[]): any[] {
    const nodeMap = new Map()
    const roots: any[] = []
    
    // Create all nodes
    nodes.forEach(node => {
      nodeMap.set(node.path, { ...node, children: [] })
    })
    
    // Build hierarchy
    nodes.forEach(node => {
      const current = nodeMap.get(node.path)
      if (node.parentPath) {
        const parent = nodeMap.get(node.parentPath)
        if (parent) {
          parent.children.push(current)
        } else {
          roots.push(current)
        }
      } else {
        roots.push(current)
      }
    })
    
    return roots
  }
}

// Singleton export
let cacheInstance: UnifiedCache | null = null

export function getUnifiedCache(): UnifiedCache {
  if (!cacheInstance) {
    cacheInstance = new UnifiedCache()
  }
  return cacheInstance
}

export type { CacheBackend, UnifiedCache }